import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import Share from './Share';
import { useGlobalModal } from '../contexts/ModalContext';
import { logEvent } from '../analytics';
import {
  getMondialiOpeningCeremonyVariant,
} from '../utils/mondialiOpeningCeremony';
import {
  MONDIALI_SHARE_ERROR_MESSAGE,
  MONDIALI_SHARE_SUCCESS_MESSAGE,
  MONDIALI_SHARE_TEXT,
  MONDIALI_SHARE_TITLE,
  getMondialiShareUrl,
  showMondialiShareSnackbar,
} from '../utils/mondialiSharing';
import modalStyles from '../styles/modal.module.css';
import styles from '../styles/MondialiOpeningCeremonyModal.module.css';
import 'flag-icons/css/flag-icons.min.css';

const POSTER_IMAGE_SRC = `${process.env.PUBLIC_URL || ''}/images/image_giochini-di-parole_800x800.png`;

const RegisteredDetails = ({ countryAlpha2, username }) => {
  const normalizedCountryAlpha2 = typeof countryAlpha2 === 'string'
    ? countryAlpha2.trim().toLowerCase()
    : '';
  const hasFlag = /^[a-z]{2}$/.test(normalizedCountryAlpha2);

  return (
    <div className={styles.registrationDetails}>
      <p className={styles.registrationLabel}>
        Sei già registrato/a con i seguenti dettagli:
        {hasFlag ? (
          <span className={styles.registrationIdentity}>
            <span className={`fi fi-${normalizedCountryAlpha2} ${styles.flag}`} aria-hidden="true" />
            <span className={styles.username}>{username}</span>
          </span>
        ) : null}
        {!hasFlag ? <span className={styles.username}> {username}</span> : null}
      </p>
    </div>
  );
};

const MondialiOpeningCeremonyModal = ({ mondiali }) => {
  const navigate = useNavigate();
  const { closeModal } = useGlobalModal();
  const variant = useMemo(() => getMondialiOpeningCeremonyVariant(mondiali), [mondiali]);

  const teamPlayersCount = Number.isInteger(mondiali?.team_players_count)
    ? mondiali.team_players_count
    : null;
  const countryNameIt = typeof mondiali?.country_name_it === 'string'
    ? mondiali.country_name_it.trim()
    : '';
  const username = typeof mondiali?.username === 'string'
    ? mondiali.username.trim()
    : '';
  const countryAlpha2 = typeof mondiali?.country_alpha2 === 'string'
    ? mondiali.country_alpha2.trim()
    : '';

  const handleRegistrationClick = () => {
    logEvent('mondiali_opening_ceremony_registration_cta_clicked', {
      source_surface: 'app_router',
      modal_name: 'mondiali_opening_ceremony',
      trigger: 'homepage_opening_ceremony_prompt',
    });
    closeModal('registration_cta_click');
    navigate('/mondiali/registrazione');
  };

  const handleShareClick = () => {
    logEvent('mondiali_opening_ceremony_share_cta_clicked', {
      source_surface: 'app_router',
      modal_name: 'mondiali_opening_ceremony',
      trigger: 'homepage_opening_ceremony_prompt',
      team_players_count: teamPlayersCount,
      country_alpha2: countryAlpha2 || null,
    });
  };

  const shareCta = (
    <div className={styles.actions}>
      <Share
        title={MONDIALI_SHARE_TITLE}
        text={MONDIALI_SHARE_TEXT}
        url={getMondialiShareUrl()}
        className={styles.primaryCta}
        buttonLabel="Invita amici"
        onShareClick={handleShareClick}
        onShareSuccess={() => showMondialiShareSnackbar(MONDIALI_SHARE_SUCCESS_MESSAGE)}
        onShareError={() => showMondialiShareSnackbar(MONDIALI_SHARE_ERROR_MESSAGE, 'error')}
      />
    </div>
  );

  if (!variant) {
    return null;
  }

  return (
    <div className={modalStyles.modalContent}>
      <div className={styles.content}>
        <p className={styles.lead}>
          Ci siamo! Le porte degli ALMA Giochini Mondiali sono ufficialmente APERTE!
        </p>

        <img
          src={POSTER_IMAGE_SRC}
          alt="Poster ALMA Giochini Mondiali"
          className={styles.poster}
        />

        <p className={styles.paragraph}>
          {variant === 'unregistered'
            ? 'Dalle 00.00 UTC del 14 giugno ogni stellina conquistata guiderà il tuo Paese verso la gloria. Le nazionali sono già in campo per il riscaldamento, partecipa anche tu!'
            : 'Dalle 00.00 UTC del 14 giugno ogni stellina conquistata guiderà il tuo Paese verso la gloria. Le nazionali sono già in campo per il riscaldamento, inizia a fare la differenza per la tua squadra!'}
        </p>

        {variant === 'unregistered' ? (
          <>
            <div className={styles.actions}>
              <Button className={styles.primaryCta} onClick={handleRegistrationClick}>
                Registrati ora
              </Button>
            </div>
            <p className={styles.paragraph}>
              Aiuta il tuo Paese a salire sul tetto del mondo e vinci premi fantastici per migliorare il tuo italiano!
            </p>
          </>
        ) : (
          <>
            <RegisteredDetails countryAlpha2={countryAlpha2} username={username} />
            {variant === 'registered_small' ? (
              <>
                {shareCta}
                <p className={styles.paragraph}>
                  Al momento ci sono {teamPlayersCount} giocatori nella tua squadra. Invita i tuoi amici e aiuta la tua nazionale a raggiungere il minimo di 5 giocatori, necessario per accedere ai premi settimanali, finali e stacanovisti.
                </p>
              </>
            ) : (
              <>
                {shareCta}
                <p className={styles.paragraph}>
                  In bocca al lupo e... forza {countryNameIt}!
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MondialiOpeningCeremonyModal;
